import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, FolderTree, Info, KeyRound, Layers, ListChecks, RefreshCcw, ScrollText, Search, UserPlus, AlertTriangle, ThumbsUp } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import infisicalIcon from '../assets/icons/infisical.svg';

const InfisicalIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleProject, setExampleProject] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [siteUrl, setSiteUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [pollInterval, setPollInterval] = useState<number | null>(null);
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);

  const [connectClientId, setConnectClientId] = useState('');
  const [connectClientSecret, setConnectClientSecret] = useState('');
  const [connectSiteUrl, setConnectSiteUrl] = useState('');

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getInfisicalStatus();
      if (data?.connected) {
        setConnected(true);
        setSiteUrl(data.site_url || '');
        setClientId(data.client_id || '');
        setPollInterval(data.poll_interval ?? null);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectInfisical({
        client_id: connectClientId.trim(),
        client_secret: connectClientSecret,
        site_url: connectSiteUrl.trim() || undefined,
      });
      setSuccessMsg('Infisical connected successfully!');
      setConnectClientSecret('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectInfisical();
      setConnected(false);
      setSuccessMsg('Infisical disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testInfisical();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Infisical (${data.project_count ?? 0} project${data.project_count === 1 ? '' : 's'} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={infisicalIcon} alt="Infisical" className="w-8 h-8 object-contain rounded-sm" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Infisical
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Manage secrets, folders, and secret syncs across projects and environments; react to secret changes, approval requests, and sync failures; and run AI investigations against Infisical</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {siteUrl && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Site URL</span>
                  <p className="font-medium truncate">{siteUrl}</p>
                </div>
              )}
              {clientId && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Machine Identity Client ID</span>
                  <p className="font-medium truncate font-mono text-xs mt-1">{clientId}</p>
                </div>
              )}
              {pollInterval != null && (
                <div>
                  <span className="text-muted-foreground">Poll Interval</span>
                  <p className="font-medium">{pollInterval >= 60 ? `${Math.round(pollInterval / 60)}m` : `${pollInterval}s`}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Resources Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Infisical actions and triggers in your workflows to automate secret hygiene, approvals, and sync monitoring. Triggers poll the Infisical audit log and project APIs — secret values are never read.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (poll-based)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Secret Created / Updated / Deleted', desc: 'Triggers on secret lifecycle events from the audit log, with the actor who made the change (audit logs require an Infisical paid plan).', Icon: KeyRound },
                  { name: 'Approval Requested', desc: 'Triggers when a secret change approval request is opened in a project.', Icon: ThumbsUp },
                  { name: 'Secret Sync Failed', desc: 'Triggers when a secret sync to an external destination fails.', Icon: AlertTriangle },
                  { name: 'Machine Identity Created', desc: 'Triggers when a new machine identity appears in the organization.', Icon: UserPlus },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get / Create / Update / Delete Secret', desc: 'Manage secrets in any project, environment, and folder. Read outputs are redacted in logs and only usable by downstream steps.', Icon: KeyRound },
                  { name: 'List Secrets', desc: 'Enumerate secret keys and metadata in a path without reading values.', Icon: ListChecks },
                  { name: 'Projects / Environments / Folders', desc: 'List projects and environments, and list or create folders.', Icon: FolderTree },
                  { name: 'Secret Syncs', desc: 'List secret syncs and trigger a sync to push secrets to a destination.', Icon: RefreshCcw },
                  { name: 'Approvals & Audit Logs', desc: 'List pending approval requests and query audit logs for reviews.', Icon: ScrollText },
                  { name: 'List Machine Identities', desc: 'Enumerate machine identities for access reviews.', Icon: Layers },
                  { name: 'Investigate Infisical', desc: 'AI investigates projects, secret metadata, syncs, and audit logs (read-only, never values).', Icon: Search },
                  { name: 'Audit Log Queries', desc: 'Pull filtered audit history into workflow context for triage.', Icon: Info },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a secret is changed in the production environment of the{' '}
              <input
                value={exampleProject}
                onChange={e => setExampleProject(e.target.value)}
                placeholder="project"
                style={{ width: `${Math.max(exampleProject.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}project in Infisical, notify{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack with the secret key, path, and the actor who changed it.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleProject || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a secret is changed in the production environment of the ${exampleProject} project in Infisical, notify ${exampleChannel} on Slack with the secret key, path, and the actor who changed it.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={infisicalIcon} alt="Infisical" className="w-8 h-8 object-contain rounded-sm" />
              <div>
                <CardTitle>Infisical</CardTitle>
                <CardDescription>Connect Infisical to manage secrets across projects and environments, monitor secret changes and sync failures, automate approval notifications, and run AI investigations. Secret values are never read by triggers.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="infisical-client-id" className="text-sm">Machine Identity Client ID</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Create a <strong className="text-foreground">Machine Identity</strong> in Infisical (<strong className="text-foreground">Organization → Access Control → Machine Identities</strong>) and add it to the projects you want to automate. Then open its <strong className="text-foreground">Universal Auth</strong> method and paste the <strong className="text-foreground">Client ID</strong> from there (not the identity's ID) here.
              </p>
              <Input
                id="infisical-client-id"
                value={connectClientId}
                onChange={e => setConnectClientId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="infisical-client-secret" className="text-sm">Machine Identity Client Secret</Label>
              <Input
                id="infisical-client-secret"
                type="password"
                value={connectClientSecret}
                onChange={e => setConnectClientSecret(e.target.value)}
                placeholder="st....."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to obtain short-lived access tokens via Universal Auth.
              </p>
            </div>
            <div>
              <Label htmlFor="infisical-site-url" className="text-sm">Site URL (optional)</Label>
              <Input
                id="infisical-site-url"
                value={connectSiteUrl}
                onChange={e => setConnectSiteUrl(e.target.value)}
                placeholder="https://app.infisical.com"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty for Infisical Cloud (US). Use <code className="bg-muted px-1 rounded text-[11px] font-mono">https://eu.infisical.com</code> for EU Cloud, or your own URL for self-hosted instances.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectClientId.trim() || !connectClientSecret}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Infisical
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default InfisicalIntegration;
